import { ExportFormat, ReportTemplate } from '../types';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useState } from 'react';
import Icon from '../../../components/AppIcon';

interface ExportPanelProps {
  formats: ExportFormat[];
  templates: ReportTemplate[];
  onExport: (format: string, template: string, fields: string[]) => void;
}

const ExportPanel = ({ formats, templates, onExport }: ExportPanelProps) => {
  const [selectedFormat, setSelectedFormat] = useState(formats[0].id);
  const [selectedTemplate, setSelectedTemplate] = useState(templates[0].id);
  const [selectedFields, setSelectedFields] = useState<string[]>(templates[0].fields);
  const [isExporting, setIsExporting] = useState(false);

  const currentTemplate = templates.find(t => t.id === selectedTemplate);

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedFields(template.fields);
    }
  };

  const handleFieldToggle = (field: string) => {
    setSelectedFields(prev =>
      prev.includes(field)
        ? prev.filter(f => f !== field)
        : [...prev, field]
    );
  };

  const handleExport = async () => {
    setIsExporting(true);
    await onExport(selectedFormat, selectedTemplate, selectedFields);
    setTimeout(() => setIsExporting(false), 2000);
  };

  return (
    <div className="bg-card rounded-md border border-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-sm bg-accent/10 flex items-center justify-center">
          <Icon name="Download" size={20} color="var(--color-accent)" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Export Report</h3>
          <p className="text-sm text-muted-foreground">Download your report in various formats</p>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Export Format
          </label>
          <div className="grid grid-cols-3 gap-3">
            {formats.map((format) => (
              <button
                key={format.id}
                onClick={() => setSelectedFormat(format.id)}
                className={`
                  p-4 rounded-sm border-2 transition-interactive hover-scale
                  ${selectedFormat === format.id
                    ? 'border-accent bg-accent/5' :'border-border hover:border-accent/50'
                  }
                `}
              >
                <Icon 
                  name={format.icon} 
                  size={24} 
                  color={selectedFormat === format.id ? 'var(--color-accent)' : 'var(--color-muted-foreground)'}
                  className="mx-auto mb-2"
                />
                <p className={`text-sm font-medium ${
                  selectedFormat === format.id ? 'text-accent' : 'text-foreground'
                }`}>
                  {format.label}
                </p>
              </button>
            ))}
          </div>
        </div>

        <div>
          <Select
            label="Report Template"
            description="Choose a preset template or customize fields"
            options={templates.map(t => ({
              value: t.id,
              label: t.name,
              description: t.description,
            }))}
            value={selectedTemplate}
            onChange={handleTemplateChange}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Include Fields
          </label>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {currentTemplate?.fields.map((field) => (
              <Checkbox
                key={field}
                label={field.replace(/([A-Z])/g, ' $1').trim()}
                checked={selectedFields.includes(field)}
                onChange={() => handleFieldToggle(field)}
              />
            ))}
          </div>
        </div>

        <Button
          variant="default"
          fullWidth
          onClick={handleExport}
          loading={isExporting}
          iconName="Download"
          iconPosition="left"
        >
          {isExporting ? 'Exporting...' : 'Export Report'}
        </Button>
      </div>
    </div>
  );
};

export default ExportPanel;